import './LeadInfo.scss';

import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import SitecoreContextFactoryService from '@services/sitecoreContextFactoryService';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import {
  DEFAULT_LAST_NAME_API_PAYLOAD_VALUE_OLD,
  HK_SITE_NAME,
  MY_SITE_NAME,
  REASON_NOT_BUY_NOW_QUESTION_CODE,
  SG_SITE_NAME,
  TW_SITE_NAME
} from '@utils/constant';
import { useFormatDate } from '@utils/customsHook/useFormatDate';

const LeadInfo = ({
  leadManagementModelFields,
  leadManagementData,
  isCPManager
}) => {
  const formatDateUtil = useFormatDate();
  const messageObj = useSelector((state) => state.getMessageReducer.objMessages);
  const currentSiteName = SitecoreContextFactoryService.getValueContextItem('site')?.name;

  const customerTypeText = useMemo(() => {
    return messageObj?.[leadManagementData?.CustomerType] || leadManagementData?.CustomerType || '';
  }, [leadManagementData, messageObj]);

  const sitesAllowedShowReasonNotBuy = useMemo(() => (
    [SG_SITE_NAME, HK_SITE_NAME, TW_SITE_NAME, MY_SITE_NAME].includes(currentSiteName)
  ), [currentSiteName]);

  const productsInterestCaptured = useMemo(() => {
    let result = {
      productNames: '',
      notBuyReasonAnswer: ''
    };

    if (sitesAllowedShowReasonNotBuy) {
      const productInterestNamesList = (
        leadManagementData?.ProductInterest || ''
      ).split(',')?.map(name => name?.trim());

      if (productInterestNamesList?.length) {
        const productInterestAllowed = leadManagementData?.CPCustomerInterest?.Products?.filter((item) => productInterestNamesList.includes(
          item.ProductName))?.map((item) => (
          {
            productName: item.ProductName,
            notBuyReasonAnswer: item.Questions?.find(question => question.QuestionCode
              === REASON_NOT_BUY_NOW_QUESTION_CODE)?.Answer?.split(',')?.map(answer => answer?.trim()) || []
          }
        )) || [];
        if (productInterestAllowed?.length) {
          result = {
            productNames: productInterestAllowed.map((item) => item.productName).join(', '),
            notBuyReasonAnswer: productInterestAllowed.reduce((reasons, item) => {
              for (const answerItem of item.notBuyReasonAnswer) {
                if (!reasons.includes(answerItem)) reasons.push(answerItem);
              }

              return reasons;
            }, [])?.join(', ')
          };
        } else {
          result = {
            productNames: leadManagementData?.ProductInterest,
            notBuyReasonAnswer: ''
          };
        }
      }
    } else {
      result = {
        productNames: leadManagementData?.ProductInterest,
        notBuyReasonAnswer: ''
      };
    }

    return result;
  }, [leadManagementData, sitesAllowedShowReasonNotBuy]);

  return (
    leadManagementModelFields && (
      <div className='lead-info'>
        <div className='lead-info__group lead-info__group--mg-bt-lg lead-info__group--space-between'>
          <div className='lead-info__group__left'>
            <h4 className='lead-info__name'>{leadManagementData.CustomerName?.replace(
              DEFAULT_LAST_NAME_API_PAYLOAD_VALUE_OLD,
              ''
            ) || ''}</h4>
            <span className='lead-info__type'>{customerTypeText}</span>
          </div>
          <a href={leadManagementData.DetailLink} className='lead-info__view-detail text-blue link-nomal'>
            <Text field={leadManagementModelFields['View Detail Text']}></Text>
          </a>
        </div>
        <div className='lead-info__group'>
          <div className='lead-info__group__block'>
            <Text tag='span' className='lead-info__label' field={leadManagementModelFields['Date Label']}></Text>
            <span className='lead-info__value'>{` ${formatDateUtil(leadManagementData.CreatedDate)}`}</span>
          </div>
          <div className='lead-info__group__block'>
            <Text tag='span' className='lead-info__label' field={leadManagementModelFields['Store Label']}></Text>
            <span className='lead-info__value'>{` ${leadManagementData.StoreName || ''}`}</span>
          </div>
          <div className='lead-info__group__block'>
            <Text
              tag='span'
              className='lead-info__label'
              field={isCPManager && isCPManager != null ? leadManagementModelFields['Staff Name Label'] : ''}
            ></Text>
            <span className='lead-info__value'>{` ${isCPManager && isCPManager != null ? leadManagementData.StaffName : ''}`}</span>
          </div>
        </div>
        <div className='lead-info__line'>
          <Text
            tag='span'
            className='lead-info__label'
            field={leadManagementModelFields['Product Interest Label']}
          ></Text>
          <span className='lead-info__value'>{` ${productsInterestCaptured.productNames || ''}`}</span>
        </div>
        <div className='lead-info__group lead-info__group--space-between'>
          {sitesAllowedShowReasonNotBuy ? (
            <div className='lead-info__group__left'>
              <Text
                tag='span'
                className='lead-info__label'
                field={leadManagementModelFields['Reasons for Not Buying']}
              ></Text>
              <span className='lead-info__value'>{` ${productsInterestCaptured.notBuyReasonAnswer}`}</span>
            </div>
          ) : (
            <div className='lead-info__group__left'>
              <Text
                tag='span'
                className='lead-info__label'
                field={leadManagementModelFields['Total Spent Label']}
              ></Text>
              <span className='lead-info__value'>{` ${leadManagementData.TotalSpent}`}</span>
            </div>
          )}

          <div className='lead-info__container'>
            {leadManagementData.IsSpent ? (
              <div className='lead-info__status'>
                <Text tag='span' className='lead-info__status__text' field={leadManagementModelFields['Purchased Label']}></Text>
              </div>
            ) : (
              <></>
            )}
            {leadManagementData.ByAppointment && (
              <div className='lead-info__status'>
                <Text tag='span' className='lead-info__status__text' field={leadManagementModelFields['By Appointment Label']}></Text>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
};

LeadInfo.propTypes = {
  leadManagementModelFields: PropTypes.any,
  leadManagementData: PropTypes.any
};

export default LeadInfo;
